import type React from "react";
import { useEffect, useState } from "react";
import {
  ArrowLeftIcon,
  ArrowNarrowRightIcon,
  GlobeIcon,
  PhotographIcon,
} from "@heroicons/react/outline";
import InteractiveViewer360 from "../../statsNew/components/gazeHeatmap/InteractiveViewer360";
import { Dropdown, Input } from "src/common/components/Input";
import RangeSelector from "src/common/components/RangeSelector/RangeSelector";
import TextArea from "src/common/components/Input/TextArea";
import type { IBlockadeLabsModel } from "../state/gallerySlice";
import {
  addBlockadeImageToDatabase,
  generateBlockadeLabsImagePreview,
  getBlockadeLabsPreview,
  mediaSelector,
  resetBlockadeLabsImagePreview,
  setCurrentMediaGallery,
} from "../state/gallerySlice";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { useTranslation } from "react-i18next";
import type { IBlockadeLabsPreview } from "../../gallery/state/gallerySlice";
import { useForm } from "react-hook-form";
import Spinner from "src/common/components/Loader/Spinner";
import InformationFrame from "src/common/components/InfomationFrame/InformationFrame";
import { setAlert } from "src/features/alert/state/alertsSlice";
import { fromUrlReturnMD5AndSize } from "../state/uploadUtil";
import { IsLoading } from "src/common/components/AppState/IsLoading";
import { FETCH_AI_MODELS_FROM_BLOCKADE_LABS } from "./GenerateMediaPanel";
import { MediaGenerationInfoParagraph } from "./MediaGenerationInfoParagraph";
import { getCurrentUser } from "src/features/profile/state/profileSlice";
import BlockadeImageToImage from "./BlockadeImageToImage";
import StepperMUI from "src/common/components/Stepper/Stepper";
import { getUserPreferences } from "src/common/util/getUserPreferences";
import type { IMedia } from "src/model/unityObject";
import { trackEvent } from "src/trackEvent";

export const GENERATE_BLOCKADE_IMAGE_COMPONENT = "generateBlockadeImageComponent";

export interface IGenerateBlockadeImagePreferences {
  defaultWorkflow: string;
}

export const GenerateBlockadeLabsImage = ({
  models = [],
  closeSliderAnimation,
}: {
  models: IBlockadeLabsModel[];
  closeSliderAnimation: () => any;
}) => {
  const currentUser = useAppSelector(getCurrentUser);
  const medias = useAppSelector(mediaSelector);

  const userPreferences = getUserPreferences({
    currentUser,
  }) as unknown as IGenerateBlockadeImagePreferences;
  const [img2Img, setImg2Img] = useState(userPreferences?.defaultWorkflow || "exNihilo");
  const [imageSource, setImageSource] = useState<File | string | null>(null);
  const [influenceValue, setInfluenceValue] = useState(0.8); // Default 80% resemblance (0.8 in RangeSelector format)
  // null = the "Quel type d'image ?" step is showing (2D vs 360° tunnel)
  const [sourceType, setSourceType] = useState<"2d" | "360" | null>(null);

  const isImg2Img = img2Img === "img2Img";
  const isChoosingType = isImg2Img && sourceType === null;
  const is2dMode = isImg2Img && sourceType === "2d";
  // img2img stepper position: 0 = type, 1 = base image, 2 = generation.
  const activeStep = !sourceType ? 0 : !imageSource ? 1 : 2;

  const toggleImg2Img = () => {
    setImg2Img(img2Img === "img2Img" ? "exNihilo" : "img2Img");
    setSourceType(null);
    setImageSource(null);
  };

  const backToTypeChoice = () => {
    setSourceType(null);
    setImageSource(null);
  };

  useEffect(() => {
    const defaultModel = [...models].find((m) => m.id === 112) || {
      id: 112,
      name: "M3 Advanced (photo/render)",
      "max-char": 2000,
      image:
        "https://images.blockadelabs.com/images/skybox/O0wGV3yQ5aVDytx1neT8BqPLnXyJuX7yR2K6Oy50.webp",
    };
    setDefaultModel(defaultModel);
    setSelectedModel(defaultModel);
  }, [models]);

  const [defaultModel, setDefaultModel] = useState(
    [...models].find((m) => m.id === 112) || {
      id: 112,
      name: "M3 Advanced (photo/render)",
      "max-char": 2000,
      image:
        "https://images.blockadelabs.com/images/skybox/O0wGV3yQ5aVDytx1neT8BqPLnXyJuX7yR2K6Oy50.webp",
    },
  );
  const [selectedModel, setSelectedModel] = useState(
    [...models].find((m) => m.id === 112) || {
      id: 112,
      name: "M3 Advanced (photo/render)",
      "max-char": 2000,
      image:
        "https://images.blockadelabs.com/images/skybox/O0wGV3yQ5aVDytx1neT8BqPLnXyJuX7yR2K6Oy50.webp",
    },
  );
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasGenerateRequestBeenSent, setGenerateRequestHasBeenSent] = useState(false);
  const [tagEditorKey, setTagEditorKey] = useState(0);
  const preview = useAppSelector(getBlockadeLabsPreview) as IBlockadeLabsPreview;
  const [isSaving, setIsSaving] = useState(false);
  const [nameUnique, setNameUnique] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { register, handleSubmit } = useForm<{ name: string }>({
    defaultValues: {
      name: "",
    },
  });

  const handleImageSelect = (value: File | string) => {
    setImageSource(value);
  };

  const cancelImageSelection = () => {
    setImageSource(null);
  };

  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPrompt(e.target.value);
  };

  const handleModelChange = (event: string) => {
    const newModel = models.find((model) => model.id === parseInt(event, 10));
    setSelectedModel(newModel || defaultModel);
  };

  const generatePreview = async () => {
    if (isGenerating || hasGenerateRequestBeenSent) return;

    trackEvent("skybox-generate");
    setIsGenerating(true);
    setGenerateRequestHasBeenSent(true);

    let imagePrompt: string | undefined;

    if (imageSource) {
      if (typeof imageSource === "string") {
        // If the image source is an S3 URL, use it as is.
        imagePrompt = imageSource;
      } else if (imageSource instanceof File) {
        // Convert local file to base64.
        imagePrompt = await new Promise<string | undefined>((resolve, reject) => {
          const fileReader = new FileReader();
          fileReader.onload = () => resolve(fileReader.result as string);
          fileReader.onerror = () => reject("Error reading file as base64");
          fileReader.readAsDataURL(imageSource);
        }).catch((error) => {
          console.error(error);
          setIsGenerating(false);
          setGenerateRequestHasBeenSent(false);
          return undefined;
        });
      }
    }

    // Convert RangeSelector value (0.01-1.0) to Blockade API init_strength (inverted:
    // higher influence = lower init_strength). Clamp to avoid floating-point drift.
    // 2D mode maps into a tighter window ([0.15, 0.5]): beyond ~0.5 Blockade
    // discards the input scene entirely (see 2d-photo-to-360 plan, Q1 spike).
    const initStrength = imagePrompt
      ? is2dMode
        ? Math.max(0.15, Math.min(0.5, 0.5 - influenceValue * 0.35))
        : Math.max(0.11, Math.min(0.9, 0.9 - influenceValue * 0.79))
      : undefined;

    const payload = {
      modelId: selectedModel.id,
      prompt,
      imagePrompt, // Include the optional imagePrompt here.
      initStrength, // Include the influence strength
      sourceMode: is2dMode ? ("2d" as const) : undefined,
      componentId: GENERATE_BLOCKADE_IMAGE_COMPONENT,
    };

    setErrorMessage("");
    dispatch(generateBlockadeLabsImagePreview(payload))
      .unwrap()
      .catch(() => {
        setIsGenerating(false);
        setGenerateRequestHasBeenSent(false);
        setErrorMessage(t("error.blockadeLabsApiError"));
      });
  };

  useEffect(() => {
    if (isGenerating) {
      if (preview && preview?.thumbnailUrl) {
        setIsGenerating(false);
      }
    }
    if (preview && preview?.faultyImageId) {
      console.error("Blockade generation error received:", preview);
      cancelPreview();
      setSelectedModel(defaultModel);

      const errorMsg = preview.errorMessage
        ? `${t("error.blockadeLabsApiError")}: ${preview.errorMessage}`
        : t("error.blockadeLabsApiError");

      setErrorMessage(errorMsg);
    }
  }, [preview]);

  useEffect(() => {
    cancelPreview();
  }, []);

  const saveImage = async (formData: { name: string }) => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      const { name } = formData;

      const nameNotAvailable = medias.find((m: IMedia) => m.name === name);

      if (nameNotAvailable) {
        setNameUnique(false);
        setIsSaving(false);
        return;
      } else {
        setNameUnique(true);
      }

      const md5AndSize = await fromUrlReturnMD5AndSize(preview.imageUrl);
      const result = await addBlockadeImageToDatabase({
        name: name,
        imageUrl: preview.imageUrl,
        thumbnailUrl: preview.thumbnailUrl,
        fileSizeInByte: md5AndSize.size,
        fileName: preview.imageUrl,
        MD5: md5AndSize.MD5,
        prompt,
        dispatch,
      });

      dispatch(setCurrentMediaGallery(result.id));
      cancelPreview();
      dispatch(
        setAlert({
          type: "success",
          msg: t("pages.gallery.generateMediaPanel.uploadBlockadeImageSuccess"),
        }),
      );
      closeSliderAnimation();
    } catch (error) {
      console.error("Error saving image:", error);
      dispatch(
        setAlert({
          type: "error",
          msg: t("pages.gallery.generateMediaPanel.uploadBlockadeImageFailure"),
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const cancelPreview = () => {
    dispatch(resetBlockadeLabsImagePreview());
    setIsGenerating(false);
    setTagEditorKey(tagEditorKey + 1);
    setGenerateRequestHasBeenSent(false);
    setNameUnique(true);
  };

  return (
    <div className="pb-6">
      {errorMessage && (
        <InformationFrame
          className="w-[90%] px-6 py-6 mx-auto absolute top-[-20px] transition-opacity z-[1000]"
          style="danger"
          onClose={() => setErrorMessage("")}
          showCloseButton
        >
          <div>{errorMessage}</div>
        </InformationFrame>
      )}
      {/* Title row — in the stepper flow the title itself carries the chosen
          image type (no redundant subtitle), and the back control sits at the
          title's level, above the stepper. */}
      <div className="flex items-center justify-between gap-4 mb-2 mt-2">
        <h3 className="font-medium text-lg">
          {t(
            !isImg2Img || preview?.thumbnailUrl
              ? "pages.gallery.generateMediaPanel.generate360Image"
              : sourceType === "2d"
              ? "pages.gallery.generateMediaPanel.generate360ImageFrom2d"
              : sourceType === "360"
              ? "pages.gallery.generateMediaPanel.generate360ImageFrom360"
              : "pages.gallery.generateMediaPanel.generate360ImageFromImage",
          )}
        </h3>
        {isImg2Img && !preview?.thumbnailUrl && (
          <button
            type="button"
            onClick={isChoosingType ? toggleImg2Img : backToTypeChoice}
            disabled={isGenerating}
            className="flex items-center text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50 shrink-0"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-1" />
            {t("general.back")}
          </button>
        )}
      </div>
      {/* Progress stepper (top of the flow, room to breathe). */}
      {isImg2Img && !preview?.thumbnailUrl && (
        <div className="mb-8">
          <StepperMUI step={activeStep} mode="generateFromImage" />
        </div>
      )}
      {/* exNihilo entry: offer to switch to the image-based flow. */}
      {!isImg2Img && !preview?.thumbnailUrl && (
        <div className="mb-4">
          <div className="font-medium text-gray-700 mt-3 mb-2">
            {t("pages.gallery.generateMediaPanel.generateFromImageTitle")}
          </div>
          <button
            type="button"
            onClick={toggleImg2Img}
            className="btn-primary-outline flex items-center gap-2"
          >
            {t("pages.gallery.generateMediaPanel.generateFromImage")}
            <ArrowNarrowRightIcon className="w-5 h-5" />
          </button>
        </div>
      )}
      {isChoosingType && (
        <div className="mb-4">
          <div className="font-medium text-gray-700 text-center mb-4">
            {t("pages.gallery.generateMediaPanel.whichImageType")}
          </div>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <button
              type="button"
              className="btn-primary-outline flex flex-col items-center gap-1 py-5"
              onClick={() => {
                // 2D flow defaults to max influence (100%) so the user's photo
                // stays as faithful to the original as possible.
                setInfluenceValue(1);
                setSourceType("2d");
              }}
            >
              <PhotographIcon className="w-8 h-8" />
              <span>{t("pages.gallery.generateMediaPanel.imageType2d")}</span>
              <span className="text-xs text-gray-500 font-normal">
                {t("pages.gallery.generateMediaPanel.imageType2dHint")}
              </span>
            </button>
            <button
              type="button"
              className="btn-primary-outline flex flex-col items-center gap-1 py-5"
              onClick={() => {
                // 360 img2img keeps the historical 80% default.
                setInfluenceValue(0.8);
                setSourceType("360");
              }}
            >
              <GlobeIcon className="w-8 h-8" />
              <span>{t("pages.gallery.generateMediaPanel.imageType360")}</span>
              <span className="text-xs text-gray-500 font-normal">
                {t("pages.gallery.generateMediaPanel.imageType360Hint")}
              </span>
            </button>
          </div>
        </div>
      )}
      {isImg2Img && sourceType && (
        <BlockadeImageToImage
          imageKind={sourceType}
          onSelect={handleImageSelect}
          onCancel={cancelImageSelection}
        />
      )}
      {isImg2Img && sourceType && imageSource && (
        <div className="mt-4 mb-4">
          <div className="flex justify-center">
            <RangeSelector
              label={t("pages.gallery.generateMediaPanel.influenceStrength")}
              value={influenceValue}
              setValue={setInfluenceValue}
              disabled={isGenerating}
              className="w-96 h-2 rounded-lg appearance-none cursor-pointer"
            />
          </div>
          <p className="text-xs text-gray-600 mt-2 text-center">
            {t("pages.gallery.generateMediaPanel.influenceDescription")}
          </p>
        </div>
      )}
      {/* Prompt/model + generate only after a base media is chosen in the
          img2img stepper (exNihilo still shows them immediately). */}
      {!preview?.thumbnailUrl && !isChoosingType && (!isImg2Img || imageSource) && (
        <div>
          <IsLoading
            componentId={FETCH_AI_MODELS_FROM_BLOCKADE_LABS}
            spinnerPlaceholder
            showSuccess={false}
          >
            {!is2dMode ? (
              <>
                <Dropdown
                  options={models.map((model) => ({
                    value: String(model.id),
                    optionText: model.name,
                  }))}
                  asDefaultValue
                  defaultValue={defaultModel.id}
                  defaultValueIndex={models.findIndex((m) => m.id === 112)}
                  label={t("pages.gallery.generateMediaPanel.selectAModel")}
                  labelFontClassName={"font-medium"}
                  value={selectedModel.id}
                  onChangeCb={handleModelChange}
                  className="mb-4"
                  noStatsAvailable={isGenerating}
                />
                {selectedModel && selectedModel?.id !== 0 && (
                  <div className="font-medium text-gray-700 mt-3">
                    <p>{t("pages.gallery.generateMediaPanel.stylePreview")}</p>
                    <img src={selectedModel.image} alt={selectedModel.name} className="my-2" />
                  </div>
                )}
              </>
            ) : (
              <></>
            )}
            {selectedModel && selectedModel?.id !== 0 ? (
              <>
                <MediaGenerationInfoParagraph
                  text={
                    is2dMode
                      ? t("pages.gallery.generateMediaPanel.promptDisclaimer2d")
                      : `${t("pages.gallery.generateMediaPanel.promptDisclaimerPt1")} ${
                          selectedModel["max-char"]
                        } ${t("pages.gallery.generateMediaPanel.promptDisclaimerPt2")}${
                          img2Img === "exNihilo"
                            ? ""
                            : " " +
                              t("pages.gallery.generateMediaPanel.promptDisclaimerPt3ImgToImg")
                        }`
                  }
                />

                <div className="font-medium text-gray-700 mt-3">
                  <p>
                    {is2dMode
                      ? t("pages.gallery.generateMediaPanel.promptOptional")
                      : t("pages.gallery.generateMediaPanel.writeYourPrompt")}
                  </p>
                  <TextArea
                    className="mt-2"
                    placeholder={t("pages.gallery.generateMediaPanel.writeYourPrompt")}
                    maxLength={selectedModel["max-char"]}
                    value={prompt}
                    onChange={handlePromptChange}
                    disabled={isGenerating}
                  />
                  <div
                    style={{
                      color: prompt.length >= selectedModel["max-char"] ? "red" : "gray",
                      marginTop: "0.5rem",
                      fontSize: "0.875rem",
                    }}
                  >
                    {prompt.length}/{selectedModel["max-char"]}
                  </div>
                </div>
              </>
            ) : (
              <></>
            )}
          </IsLoading>
        </div>
      )}
      {!hasGenerateRequestBeenSent ? (
        !isChoosingType &&
        (!isImg2Img || imageSource) && (
          <button
            disabled={
              !selectedModel ||
              (!is2dMode && !prompt) ||
              isGenerating ||
              (isImg2Img && !imageSource)
            }
            className={`btn-primary-fill mr-3 mt-2 ${
              selectedModel.id === 0 || (!is2dMode && prompt.length === 0)
                ? "cursor-not-allowed"
                : ""
            }`}
            onClick={generatePreview}
          >
            {t("general.generate")}
          </button>
        )
      ) : preview?.thumbnailUrl ? (
        <div className="mt-4">
          <form onSubmit={handleSubmit(saveImage)}>
            <div className="block font-medium text-gray-700 ">{t("general.fileName")}</div>
            <Input
              className="mb-4"
              name="name"
              type="text"
              maxLength={30}
              placeholder={t("general.fileName")}
              autoFocus
              required
              registerFct={() => register("name", { required: "Name is required" })}
            />
            <div className={`text-red-600 ${nameUnique ? "invisible" : ""}`}>
              {t("general.mediaNameAlreadyUsed")}
            </div>

            <div>
              <p className="font-medium text-gray-700 mt-3">
                {t("pages.gallery.generateMediaPanel.yourGeneratedImage")}
              </p>
              <div className="relative w-full h-[350px] bg-black overflow-hidden">
                <InteractiveViewer360
                  mediaUrl={preview.imageUrl}
                  mediaType="image"
                  heatmapImageData={null}
                  opacity={0}
                  showFullscreenButton
                />
              </div>
            </div>

            <button className={"btn-primary-fill mr-3 mt-2"} type="submit" disabled={isSaving}>
              {t("general.save")}
            </button>
            <button
              className={"btn-primary-outline mr-3 mt-2"}
              onClick={cancelPreview}
              type="button"
            >
              {t("general.cancel")}
            </button>
          </form>
        </div>
      ) : (
        <div className="flex flex-col items-center mt-4">
          {/* Spinner sits in its own sized, relatively-positioned box so it
              renders right above the notice instead of the shared Spinner's
              absolute centering floating it to the panel middle. */}
          <div className="relative h-11 w-11">
            <Spinner
              // Center via negative margins, NOT transform: the shared Spinner
              // uses `animate-spin`, which animates the transform property, so a
              // translate here is overridden mid-keyframe and the spinner slides
              // /orbits instead of spinning in place. h-10 w-10 → offset -1.25rem.
              style={{
                marginLeft: "-1.25rem",
                marginTop: "-1.25rem",
                marginRight: 0,
              }}
            />
          </div>
          {is2dMode && (
            <p className="text-sm text-gray-600 mt-2 text-center">
              {t("pages.gallery.generateMediaPanel.generating2dNotice")}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
